import React from "react";
import PolicyTemplate from "./PolicyTemplate";
import siteConfig from "../config/siteConfig";
import "../css/terms.scss";

const brand = siteConfig.brandName;

const termsPolicy = {
  eyebrow: "Legal & responsibility",
  title: "Terms of service",
  intro: `These terms set out the agreement between you and ${brand} when you browse this site, place an order, or use a related service.`,
  updated: "October 23, 2026",
  className: "terms-page",
  sections: [
    {
      id: "terms-agreement",
      title: "Agreement and store operator",
      paragraphs: [
        `By using the site, you agree to these terms and the policies incorporated by reference. The storefront is presented under the ${brand} brand and managed from ${siteConfig.operatingRegion.country}. If you do not agree, do not use the site or submit an order.`,
        "Headings are for convenience. “Including” means including without limitation. Electronic records and communications may be used where law permits.",
      ],
      links: [{ prefix: "Operator contact information is in our ", to: "/legal-notice", label: "legal notice", suffix: "." }],
    },
    {
      id: "terms-eligibility",
      title: "Eligibility and customer information",
      paragraphs: [
        "You must have legal capacity to enter the purchase contract, or act with the involvement of a parent or legal guardian. Information supplied at checkout must be current, accurate, and yours to provide. You are responsible for reviewing delivery, product, quantity, and contact details before payment.",
      ],
    },
    {
      id: "terms-orders",
      title: "Orders and acceptance",
      paragraphs: [
        "Your order is an offer to purchase. An automated receipt confirms that we received it; acceptance occurs when we send dispatch confirmation, unless mandatory law provides otherwise. We may verify details, apply published purchase limits, or reject an order before acceptance.",
        "We may cancel because of unavailable inventory, a material description or pricing error, payment failure, suspected fraud, restricted destination, policy abuse, or circumstances outside reasonable control. Amounts collected for cancelled items will be returned to the original payment method.",
      ],
    },
    {
      id: "terms-prices",
      title: "Prices, payment, and taxes",
      paragraphs: [
        `Prices are shown in ${siteConfig.currency.code} unless stated otherwise. Delivery and taxes collected by us are shown before payment. Your issuer may charge conversion or transaction fees, and a destination authority may assess duties or taxes not collected at checkout.`,
        "You authorize the payment provider to process the submitted amount. Complete card numbers and security codes are entered with the payment provider and are not stored by this storefront.",
      ],
      links: [{ prefix: "See ", to: "/payments", label: "payments & billing", suffix: " for operational details." }],
    },
    {
      id: "terms-products",
      title: "Products and availability",
      paragraphs: [
        "We aim to present product color, scale, materials, compatibility, and details accurately. Screens and natural variation in paper, textile, leather, wood, metal, or finish can create reasonable differences. A disclosed natural characteristic is not automatically a defect.",
        "Availability, editions, and purchase limits may change before acceptance. We will not substitute a materially different product without your agreement. Pre-order estimates are estimates and are governed by the pre-order policy.",
      ],
    },
    {
      id: "terms-delivery",
      title: "Delivery and risk",
      paragraphs: [
        "Processing and transit estimates are not guaranteed unless expressly described as such. Provide a deliverable address and cooperate with lawful carrier or customs requests. Risk passes only when required by the law applicable to the transaction; nothing here shifts risk earlier than mandatory law permits.",
      ],
      links: [{ prefix: "Current methods and delay guidance are in ", to: "/shipping", label: "shipping information", suffix: "." }],
    },
    {
      id: "terms-returns",
      title: "Returns, faults, and cancellation rights",
      paragraphs: [
        "Our returns policy provides a voluntary return window for eligible goods. Separate mandatory remedies may apply when goods are faulty, unsafe, not as described, or not supplied. Consumers in some locations may also have a statutory distance-selling withdrawal right. No final-sale label or other store policy removes a right that cannot lawfully be excluded.",
      ],
      links: [{ prefix: "Review ", to: "/returns", label: "returns & exchanges", suffix: " before sending an item." }],
    },
    {
      id: "terms-promotions",
      title: "Promotions, gift cards, and credit",
      paragraphs: [
        "Promotions and stored-value products are subject to the specific terms disclosed with them. Codes may be limited by time, products, customer, region, or redemption count. Gift cards and store credit are not cash and may be restricted or refundable only as stated and allowed by law.",
      ],
    },
    {
      id: "terms-use",
      title: "Acceptable use",
      bullets: [
        "do not break the law, infringe rights, misrepresent identity, or submit unauthorized payment information;",
        "do not introduce malicious code, overwhelm, probe, bypass, or disrupt the site or connected services;",
        "do not scrape protected content, automate purchases, evade purchase limits, or interfere with another customer; and",
        "do not use site content or branding to create confusion about source, sponsorship, or affiliation.",
      ],
      paragraphs: [
        "We may restrict access or cancel affected orders when reasonably necessary to protect customers, the service, or legal rights. This does not prevent good-faith security reporting under our security disclosure policy.",
      ],
    },
    {
      id: "terms-property",
      title: "Intellectual property and submissions",
      paragraphs: [
        `Original site materials belong to ${brand} or the identified rights holder and are provided for personal shopping use. You retain ownership of material you submit. You give us a non-exclusive, worldwide, royalty-free license to host, reproduce, adapt, and communicate that material only as reasonably needed to provide the requested service, display an authorized review, administer rights, or meet legal obligations.`,
        "You must have the right to submit content, and it must not be unlawful, deceptive, confidential without authority, or infringing. A separate notice process is available for rights owners.",
      ],
    },
    {
      id: "terms-third-party",
      title: "Third-party services",
      paragraphs: [
        "Payment, delivery, and linked services may be operated by independent providers under their own terms and privacy notices. We are responsible for our obligations as seller, but do not control an independent provider’s unrelated service or content. A link does not imply endorsement unless expressly stated.",
      ],
    },
    {
      id: "terms-liability",
      title: "Service availability and liability",
      paragraphs: [
        "We may maintain, correct, update, or temporarily suspend the site. To the extent permitted by law, we are not responsible for indirect or unforeseeable loss caused by events beyond reasonable control or by use contrary to instructions. Any liability limit applies only to the extent lawful and reasonable in the circumstances.",
        "Nothing in these terms excludes or limits liability or a remedy that cannot legally be excluded, including applicable liability for fraud, deliberate misconduct, death or personal injury caused by negligence, or mandatory consumer guarantees.",
      ],
    },
    {
      id: "terms-force",
      title: "Events outside reasonable control",
      paragraphs: [
        "We are not in breach for delay caused by events reasonably outside our control, such as carrier interruption, severe weather, disaster, labor disruption, governmental action, cyber incident, utility failure, epidemic, or supplier failure. We will take reasonable steps to reduce the effect, communicate a material delay, and provide cancellation or refund where required.",
      ],
    },
    {
      id: "terms-law",
      title: "Governing law and disputes",
      paragraphs: [
        "These terms are governed by Hong Kong law where that choice is valid. Hong Kong courts have non-exclusive jurisdiction. If you are a consumer elsewhere, this choice does not remove mandatory protections or access to a competent court or authority that cannot be excluded in your usual place of residence.",
        "Before starting a formal claim, we invite you to use the complaints process so we can try to resolve the issue. It is optional and does not suspend a statutory deadline or require arbitration.",
      ],
    },
    {
      id: "terms-general",
      title: "Changes, severability, and contact",
      paragraphs: [
        "We may update these terms prospectively. The version in effect when an order is placed continues to govern that order unless a change is required by law or agreed with you. If a provision is invalid, the remainder continues and the provision is limited only as much as necessary. A delay in enforcement is not a waiver.",
      ],
      email: true,
      emailSuffix: " for questions about these terms.",
    },
  ],
  related: [
    { to: "/legal-notice", label: "Legal notice" },
    { to: "/privacy", label: "Privacy policy" },
    { to: "/complaints", label: "Complaints process" },
  ],
};

function Terms() {
  return <PolicyTemplate policy={termsPolicy} />;
}

export default Terms;
